/**
 * @fileoverview Página de DRE Editável
 * @module pages/DREEditavel
 * 
 * Página dedicada para edição de DRE histórica com versionamento e auto-save
 */

import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Stack,
  Box,
  Alert,
  Divider,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import { 
  ArrowForward as ArrowForwardIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';

import { useDREStore } from '../stores/dreStore';
import { 
  DREEditableTable,
  SaveVersionButton,
  VersionHistory,
  SaveStatusIndicator,
} from '../components/dre';
import { useAutoSave } from '../hooks/useAutoSave';

export function DREEditavel() {
  const { loadDRE, isLoading, dreData } = useDREStore();
  const hasLoadedRef = useRef(false);
  
  // Auto-save DESABILITADO - salvamento apenas manual pelo usuário
  const { status: autoSaveStatus, forceSave } = useAutoSave({
    enabled: false, // ❌ Auto-save desabilitado
    debounceMs: 5000,
    versionNamePrefix: 'Auto-save'
  });

  // ✅ CORREÇÃO: Apenas carregar se dados NÃO existirem E ainda não carregou
  // Evita sobrescrever edições em memória ao navegar entre páginas
  useEffect(() => {
    if (!dreData && !hasLoadedRef.current) {
      console.log('[DREEditavel] 📥 Primeira carga: carregando dados...');
      hasLoadedRef.current = true;
      loadDRE();
    } else if (dreData) {
      console.log('[DREEditavel] ✅ Dados já existem em memória, preservando edições');
    }
  }, [dreData, loadDRE]);

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Carregando dados históricos...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* ✅ MOVIDO: ContextPanel agora está integrado no AppHeader (global em todas as páginas) */}

        {/* Header */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" fontWeight="bold">
              DRE Editável
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <SaveStatusIndicator 
                isSaving={autoSaveStatus.isSaving}
                lastSaved={autoSaveStatus.lastSaved}
                hasUnsavedChanges={autoSaveStatus.hasUnsavedChanges}
              />
              <SaveVersionButton onSave={forceSave} />
            </Stack>
          </Stack>
          
          {/* Instruções de uso */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Modo de Edição:</strong> Clique em "Editar DRE" para começar. 
              Você pode editar valores, adicionar ou remover anos. 
              Use o botão "Salvar Versão" para criar snapshots das suas alterações.
            </Typography>
          </Alert>

          {/* Alerta de impacto global */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <WarningIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              <strong>Impacto Global:</strong> As alterações feitas aqui afetarão{' '}
              <strong>todas as páginas da aplicação</strong> (Histórico, Dashboard, Premissas e Projeções).
              Os cálculos de valuation serão recalculados automaticamente com os novos dados.
            </Typography>
            <Typography variant="caption" display="block" mt={1} color="text.secondary">
              <LightbulbIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Dica: Use o botão "Salvar Versão" para criar snapshots antes de fazer alterações grandes.
              Você poderá restaurar versões antigas a qualquer momento.
            </Typography>
            {/* SPRINT 3 - Bug #3: Link para Premissas */}
            <Box mt={1.5}>
              <RouterLink to="/premissas" style={{ textDecoration: 'none' }}>
                <MuiLink
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  Ver como isso afeta as Premissas
                  <ArrowForwardIcon fontSize="small" />
                </MuiLink>
              </RouterLink>
            </Box>
          </Alert>
        </Box>

        {/* DRE Sétima */}
        <DREEditableTable company="setima" />

        {/* DRE 555 Studios */}
        <DREEditableTable company="555" />

        <Divider sx={{ my: 4 }} />

        {/* Histórico de Versões */}
        <Box>
          <Typography variant="h5" fontWeight="bold" mb={3}>
            Histórico de Versões
          </Typography>
          <VersionHistory />
        </Box>
      </Stack>
    </Container>
  );
}
